import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Mail, Calculator } from 'lucide-react';

const STATUS_COLORS = {
  strong: { bg: 'bg-brand-surface', text: 'text-brand-dark', border: 'border-border' },
  adequate: { bg: 'bg-surface-app', text: 'text-status-info', border: 'border-border' },
  weak: { bg: 'bg-surface-app', text: 'text-status-warning', border: 'border-border' },
  critical: { bg: 'bg-surface-app', text: 'text-status-critical', border: 'border-border' },
};

const EFFORT_COLORS = { low: 'text-brand-dark', medium: 'text-status-warning', high: 'text-status-critical' };

/**
 * Deterministic readiness band. Thresholds are fixed and published in the UI
 * (assessment.bandNote) and mirrored by the backend (services/assessment.readiness_band).
 * It is NOT a comparison with other organisations.
 */
export const READINESS_THRESHOLDS = { advanced: 75, established: 55, developing: 35 };

export function readinessBand(score) {
  if (score >= READINESS_THRESHOLDS.advanced) return 'advanced';
  if (score >= READINESS_THRESHOLDS.established) return 'established';
  if (score >= READINESS_THRESHOLDS.developing) return 'developing';
  return 'early';
}

function scoreColor(score) {
  if (score >= 60) return '#78B928';
  if (score >= 40) return '#B7791F';
  return '#B42318';
}

function ScoreRing({ score }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = scoreColor(score);
  return (
    <div className="relative w-36 h-36 mx-auto">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#DDE1E5" strokeWidth="8" />
        <circle
          cx="60" cy="60" r={r}
          fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-semibold text-[#171A1D]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{score}</span>
        <span className="text-xs text-[#8A929B]">/ 100</span>
      </div>
    </div>
  );
}

function DomainRow({ domain, t }) {
  const c = STATUS_COLORS[domain.status] || STATUS_COLORS.adequate;
  const statusLabel = t('assessment.status.' + domain.status, domain.status);
  return (
    <div className={`p-4 rounded-lg border ${c.border} ${c.bg}`}>
      <div className="flex flex-wrap gap-3 items-center justify-between mb-1">
        <span className="text-sm font-semibold text-[#171A1D]">{domain.name}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-[#8A929B]">{domain.score}/100</span>
          <span className={`text-xs font-bold uppercase ${c.text}`}>{statusLabel}</span>
        </div>
      </div>
      <p className="text-xs text-[#8A929B] leading-relaxed">{domain.finding}</p>
    </div>
  );
}

/** Final assessment results: ring, breakdown, priorities, recommendation, actions. */
export function ResultsStep({ result, emailing, onEmail, onUseInCalculator }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6" data-testid="assessment-results">
      {/* Score */}
      <div className="text-center mb-4">
        <h1 className="text-2xl font-semibold mb-6 text-[#171A1D]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {t('assessment.readinessScore')}
        </h1>
        <ScoreRing score={result.overall_score} />
        <p className="mt-4 text-sm text-[#3A4249] max-w-md mx-auto leading-relaxed">{result.summary}</p>
      </div>

      {/* Readiness position — deterministic band derived from the score, never a population statistic */}
      <Card className="bg-white border-[#DDE1E5]" data-testid="readiness-position">
        <CardContent className="p-5">
          <h3 className="text-xs text-[#8A929B] uppercase tracking-wider mb-3">{t('assessment.readinessPosition')}</h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="text-center">
              <div className="text-2xl font-semibold text-[#527F19]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="readiness-score-value">
                {result.overall_score}
              </div>
              <div className="text-xs text-[#8A929B]">{t('assessment.yourScore')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-[#171A1D]" data-testid="readiness-band-value">
                {t(`assessment.band.${readinessBand(result.overall_score)}`)}
              </div>
              <div className="text-xs text-[#8A929B]">{t('assessment.readinessBand')}</div>
            </div>
          </div>
          <p className="text-xs text-[#8A929B] leading-relaxed" data-testid="readiness-band-note">{t('assessment.bandNote')}</p>
        </CardContent>
      </Card>

      {/* Domain Breakdown */}
      <Card className="bg-white border-[#DDE1E5]" data-testid="domain-breakdown">
        <CardContent className="p-5">
          <h3 className="text-xs text-[#8A929B] uppercase tracking-wider mb-3">{t('assessment.domainBreakdown')}</h3>
          <div className="space-y-2">
            {(result.domains || []).map((d) => (
              <DomainRow key={`${d.name}-${d.score}`} domain={d} t={t} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Priority Actions */}
      <Card className="bg-white border-[#DDE1E5]" data-testid="priority-actions">
        <CardContent className="p-5">
          <h3 className="text-xs text-[#8A929B] uppercase tracking-wider mb-3">{t('assessment.priorityActions')}</h3>
          <div className="space-y-3">
            {(result.priority_actions || []).map((a) => (
              <div key={`${a.rank}-${a.action}`} className="flex gap-3 p-3 rounded-lg bg-[#F5F6F7] border border-[#DDE1E5]">
                <div className="text-lg font-semibold text-[#527F19] shrink-0 w-7" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>#{a.rank}</div>
                <div className="flex-1">
                  <p className="text-sm text-[#171A1D] font-medium">{a.action}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={`text-xs font-semibold ${EFFORT_COLORS[a.effort] || 'text-[#8A929B]'}`}>
                      {t('assessment.effort')}: {t('assessment.effortLevel.' + a.effort, a.effort)}
                    </span>
                    <span className="text-xs text-[#8A929B]">{a.timeline}</span>
                  </div>
                  <p className="text-xs text-[#66707A] mt-1">{a.impact}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recommendation */}
      <Card className="bg-white border-[#DDE1E5]">
        <CardContent className="p-5 text-center">
          <h3 className="text-xs text-[#8A929B] uppercase tracking-wider mb-2">{t('assessment.recommendedApproach')}</h3>
          <div className="text-lg font-bold text-[#171A1D]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {t('assessment.approach.' + result.recommended_approach, result.recommended_approach)} {t('assessment.implementation')}
          </div>
          <p className="text-sm text-[#66707A] mt-1">{t('assessment.estimatedTimeline', { months: result.estimated_months })}</p>
        </CardContent>
      </Card>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          className="flex-1 h-11 bg-[#527F19] hover:bg-[#3F6114] text-white font-semibold"
          onClick={onUseInCalculator}
          data-testid="use-in-calculator-btn"
        >
          <Calculator className="w-4 h-4 mr-2" /> {t('assessment.useInCalculator')}
        </Button>
        <Button
          variant="outline"
          className="flex-1 h-11 border-[#DDE1E5] text-[#66707A] hover:text-[#171A1D] hover:bg-[#F5F6F7]"
          onClick={onEmail}
          disabled={emailing}
          data-testid="email-report-btn"
        >
          {emailing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
          {t('assessment.emailReport')}
        </Button>
      </div>
    </div>
  );
}
